import React, { createContext, useContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { AppState, FocusAreas, PlantSettings, PlantLocation } from './types.ts';
import { INITIAL_STATE } from './types.ts';
import { saveToStorage, loadFromStorage } from './storage.ts';

interface AppContextType {
  state: AppState;
  setCountry: (country: string) => void;
  setRegion: (region: string) => void;
  setDistrict: (district: string) => void;
  setObjective: (objective: string) => void;
  setWasteCategory: (category: string) => void;
  setWasteType: (type: string) => void;
  setBusinessModel: (model: string) => void;
  setBusinessModels: (models: string[]) => void;
  toggleBusinessModel: (model: string, maxSelection?: number) => void;
  updateFocusArea: <K extends keyof FocusAreas>(area: K, value: FocusAreas[K]) => void;
  updateMultipleFocusAreas: (updates: Partial<FocusAreas>) => void;
  updatePlantSetting: <K extends keyof PlantSettings>(setting: K, value: PlantSettings[K]) => void;
  setPlantLocation: (location: PlantLocation | null) => void;
  resetState: () => void;
}

type Action =
  | { type: 'LOAD_STATE'; payload: AppState }
  | { type: 'SET_COUNTRY'; payload: string }
  | { type: 'SET_REGION'; payload: string }
  | { type: 'SET_DISTRICT'; payload: string }
  | { type: 'SET_OBJECTIVE'; payload: string }
  | { type: 'SET_WASTE_CATEGORY'; payload: string }
  | { type: 'SET_WASTE_TYPE'; payload: string }
  | { type: 'SET_BUSINESS_MODEL'; payload: string }
  | { type: 'SET_BUSINESS_MODELS'; payload: string[] }
  | { type: 'TOGGLE_BUSINESS_MODEL'; payload: { model: string; maxSelection: number } }
  | { type: 'UPDATE_FOCUS_AREA'; payload: { area: keyof FocusAreas; value: number } }
  | { type: 'UPDATE_MULTIPLE_FOCUS_AREAS'; payload: Partial<FocusAreas> }
  | { type: 'UPDATE_PLANT_SETTING'; payload: { setting: keyof PlantSettings; value: string | number } }
  | { type: 'SET_PLANT_LOCATION'; payload: PlantLocation | null }
  | { type: 'RESET_STATE' };

const appReducer = (state: AppState, action: Action): AppState => {
  switch (action.type) {
    case 'LOAD_STATE':
      return action.payload;

    case 'SET_COUNTRY':
      return {
        ...state,
        selectedCountry: action.payload,
        selectedRegion: '',
        selectedObjective: '',
        selectedWasteCategory: '',
        selectedWasteType: '',
        selectedBusinessModel: '',
        selectedBusinessModels: [],
        plantLocation: null,
      };

    case 'SET_REGION':
      return {
        ...state,
        selectedRegion: action.payload,
        selectedDistrict: '',
        selectedObjective: '',
        selectedWasteCategory: '',
        selectedWasteType: '',
        selectedBusinessModel: '',
        selectedBusinessModels: [],
        plantLocation: null,
      };

    case 'SET_DISTRICT':
      return {
        ...state,
        selectedDistrict: action.payload,
      };

    case 'SET_OBJECTIVE':
      return {
        ...state,
        selectedObjective: action.payload,
      };

    case 'SET_WASTE_CATEGORY':
      return {
        ...state,
        selectedWasteCategory: action.payload,
        selectedWasteType: '',
        selectedBusinessModel: '',
        selectedBusinessModels: [],
      };

    case 'SET_WASTE_TYPE':
      return {
        ...state,
        selectedWasteType: action.payload,
        selectedBusinessModels: [],
        selectedBusinessModel: state.selectedBusinessModel === 'Wastewater Reuse' ||
          state.selectedBusinessModel === 'Alternative Proteins'
          ? '' : state.selectedBusinessModel,
      };

    case 'SET_BUSINESS_MODEL':
      return {
        ...state,
        selectedBusinessModel: action.payload,
        selectedBusinessModels: [],
      };

    case 'SET_BUSINESS_MODELS':
      return {
        ...state,
        selectedBusinessModels: action.payload,
      };

    case 'TOGGLE_BUSINESS_MODEL':
      const { model, maxSelection } = action.payload;
      const currentModels = state.selectedBusinessModels;
      let newModels;

      // Controlla se il model esiste già confrontando per type
      const existingIndex = currentModels.findIndex(
        (m) => m.type === model.type
      );

      if (existingIndex !== -1) {
        // Rimuovi se esiste
        newModels = currentModels.filter((m) => m.type !== model.type);
      } else {
        // Aggiungi se c'è spazio
        if (currentModels.length < maxSelection) {
          newModels = [...currentModels, model];
        } else {
          newModels = currentModels;
        }
      }

      return {
        ...state,
        selectedBusinessModels: newModels,
      };
  
    case 'UPDATE_FOCUS_AREA':
      return {
        ...state,
        focusAreas: {
          ...state.focusAreas,
          [action.payload.area]: action.payload.value,
        },
      };

    case 'UPDATE_MULTIPLE_FOCUS_AREAS':
      return {
        ...state,
        focusAreas: {
          ...state.focusAreas,
          ...action.payload,
        },
      };

    case 'UPDATE_PLANT_SETTING':
      return {
        ...state,
        plantSettings: {
          ...state.plantSettings,
          [action.payload.setting]: action.payload.value,
        },
      };

    case 'SET_PLANT_LOCATION':
      return {
        ...state,
        plantLocation: action.payload,
      };

    case 'RESET_STATE':
      return INITIAL_STATE;

    default:
      return state;
  }
};

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // const [state, dispatch] = useReducer(appReducer, INITIAL_STATE);
  const [state, dispatch] = useReducer(appReducer, loadFromStorage());

  // useEffect(() => {
  //   const savedState = loadFromStorage();
  //   dispatch({ type: 'LOAD_STATE', payload: savedState });
  // }, []);
  useEffect(() => {
    saveToStorage(state);
  }, [state]);

  
  const setCountry = (country: string) => {
    dispatch({ type: 'SET_COUNTRY', payload: country });
  };

  const setRegion = (region: string) => {
    dispatch({ type: 'SET_REGION', payload: region });
  };

  const setDistrict = (district: string) => {
    dispatch({ type: 'SET_DISTRICT', payload: district });
  };

  const setObjective = (objective: string) => {
    dispatch({ type: 'SET_OBJECTIVE', payload: objective });
  };

  const setWasteCategory = (category: string) => {
    dispatch({ type: 'SET_WASTE_CATEGORY', payload: category });
  };

  const setWasteType = (type: string) => {
    dispatch({ type: 'SET_WASTE_TYPE', payload: type });
  };

  const setBusinessModel = (model: string) => {
    dispatch({ type: 'SET_BUSINESS_MODEL', payload: model });
  };

  const setBusinessModels = (models: string[]) => {
    dispatch({ type: 'SET_BUSINESS_MODELS', payload: models });
  };

  const toggleBusinessModel = (model: string, maxSelection: number = 5) => {
    dispatch({ type: 'TOGGLE_BUSINESS_MODEL', payload: { model, maxSelection } });
  };

  const updateFocusArea = <K extends keyof FocusAreas>(area: K, value: FocusAreas[K]) => {
    dispatch({ type: 'UPDATE_FOCUS_AREA', payload: { area, value } });
  };

  const updateMultipleFocusAreas = (updates: Partial<FocusAreas>) => {
    dispatch({ type: 'UPDATE_MULTIPLE_FOCUS_AREAS', payload: updates });
  };

  const updatePlantSetting = <K extends keyof PlantSettings>(setting: K, value: PlantSettings[K]) => {
    dispatch({ type: 'UPDATE_PLANT_SETTING', payload: { setting, value } });
  };

  const setPlantLocation = (location: PlantLocation | null) => {
    dispatch({ type: 'SET_PLANT_LOCATION', payload: location });
  };

  const resetState = () => {
    dispatch({ type: 'RESET_STATE' });
  };

  const contextValue: AppContextType = {
    state,
    setCountry,
    setRegion,
    setDistrict,
    setObjective,
    setWasteCategory,
    setWasteType,
    setBusinessModel,
    setBusinessModels,
    toggleBusinessModel,
    updateFocusArea,
    updateMultipleFocusAreas,
    updatePlantSetting,
    setPlantLocation,
    resetState,
  };

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
